import React from "react";

import {
  APP_ID_ALL,
  APP_ID_DYNAMIC_CREATIVE,
  APP_ID_PLAYER,
  APP_ID_SINGLE_ZONE_SCHEDULER,
  APP_ID_VISTAR_PLAYER,
  APP_ID_VSA_BACKUP_LOOP,
  APP_ID_VSA_SCHEDULE,
} from "cortex/ui/src/constant/application";
import { sortByLabelAsc } from "cortex/ui/src/util/sortByProp";

const sources = {
  ALL_APPS_ID: APP_ID_ALL,
  DYNAMIC_CREATIVE_APP_ID: APP_ID_DYNAMIC_CREATIVE,
  PLAYER_APP_ID: APP_ID_PLAYER,
  SZS_APP_ID: APP_ID_SINGLE_ZONE_SCHEDULER,
  APP_ID_VSA_BACKUP_LOOP: APP_ID_VSA_BACKUP_LOOP,
  APP_ID_VSA_SCHEDULE: APP_ID_VSA_SCHEDULE,
  APP_ID_VISTAR_PLAYER: APP_ID_VISTAR_PLAYER,
};

const sourceOptionsMap = [
  { label: "Player", value: sources.PLAYER_APP_ID },
  {
    label: "Dynamic Creative",
    value: sources.DYNAMIC_CREATIVE_APP_ID,
  },
  {
    label: "VSA Schedule",
    value: sources.APP_ID_VSA_SCHEDULE,
  },
  {
    label: "VSA Backup Loop",
    value: sources.APP_ID_VSA_BACKUP_LOOP,
  },
  {
    label: "Vistar Player",
    value: sources.APP_ID_VISTAR_PLAYER,
  },
  { label: "All applications", value: sources.ALL_APPS_ID },
];

const SEVERITY_INFO = {
  label: "Information",
  value: "0",
};
const SEVERITY_WARN = {
  label: "Warning",
  value: "1",
};
const SEVERITY_CRIT = {
  label: "Critical",
  value: "2",
};
const SEVERITY_OPTIONS = [SEVERITY_INFO, SEVERITY_WARN, SEVERITY_CRIT];

const DATE_RANGE_MAX_DAYS = 30;
const DATE_RANGE_FIELD_NAME = "event_date";

const customAlertPreset = {
  label: "Add a custom alert",
  description: "",
  eventNames: [],
  frequency: 0,
  name: "",
  priorities: [],
  sources: [],
  threshold: 10,
};

const alertPresetsList = [
  {
    label: "Critical Events",
    description:
      "Notify users immediately whenever something critical " +
      "happens on a device.",
    eventNames: ["*"],
    frequency: 1,
    priorities: [Number(SEVERITY_CRIT.value)],
    sources: [],
    threshold: 1,
  },
  {
    label: "Player has been silent",
    description:
      "Notify users if a player doesn't raise any events in the " +
      "past 5 minutes. Not getting any events in 5 minutes is highly unusual " +
      "and should be considered a critical situation.",
    eventNames: ["*"],
    frequency: 0,
    priorities: [],
    sources: [],
    threshold: 5,
  },
  {
    label: "No content has been rendered",
    description: (
      <span>
        Notify users if a player does not raise any content-rendered events in
        the past 10 minutes.
        <br /> <strong>NOTE</strong>: Please update the threshold to be greater
        than 10 minutes if you are expecting to play assets for longer than 10
        minutes.
      </span>
    ),
    eventNames: ["content-rendered"],
    frequency: 0,
    priorities: [],
    sources: [],
    threshold: 10,
  },
  {
    label: "Player restarting too often",
    description:
      "Notify users if a player has been restarting too many " +
      "times in the last day.",
    eventNames: ["player-restart"],
    frequency: 3,
    priorities: [],
    sources: [APP_ID_PLAYER],
    threshold: 60 * 24,
  },
  {
    label: "Important player events",
    description:
      "Notify users immediately whenever a player raises a warning event.",
    eventNames: ["*"],
    frequency: 2,
    priorities: [1],
    sources: [APP_ID_PLAYER],
    threshold: 10,
  },
  {
    label: "Player / Watchdog update failed",
    description: "Notify users immediately when software updates fail.",
    eventNames: [
      "player-update-failed",
      "player-update-cleanup-failed",
      "watchdog-update-failed",
      "watchdog-update-cleanup-failed",
    ],
    frequency: 1,
    priorities: [],
    sources: [APP_ID_PLAYER],
    threshold: 1,
  },
  {
    label: "Apps unable to cache content",
    description:
      "Notify users when an app fails to cache content 3 times " +
      "in the last hour.",
    eventNames: ["app-cache-failed"],
    frequency: 3,
    priorities: [],
    sources: [],
    threshold: 60,
  },
  {
    label: "Ad requests are failing",
    description: "Notify users when ad requests are failing.",
    eventNames: ["ad-request-failed"],
    frequency: 60,
    priorities: [],
    sources: [],
    threshold: 60,
  },
  {
    label: "Image assets are too big",
    description:
      "Notify users immediately whenever the ads app gets a " +
      "large image file or the file size check fails.",
    eventNames: ["ad-image-too-large", "ad-image-size-check-failed"],
    frequency: 1,
    priorities: [],
    sources: [],
    threshold: 1,
  },
  {
    label: "Ads failing to play",
    description: "Notify users when the ads app fails to play ads.",
    eventNames: ["video-ad-failed*", "bundle-ad-failed*", "image-ad-failed*"],
    frequency: 2,
    priorities: [],
    sources: [],
  },
  {
    label: "Applications can't prepare content",
    description: "Notify users when one or more apps can't prepare content.",
    eventNames: ["prepare-timed-out", "prepare-call-failed"],
    frequency: 1,
    priorities: [],
    sources: [APP_ID_SINGLE_ZONE_SCHEDULER],
    threshold: 1,
  },
  {
    label: "Content render issues",
    description:
      "Notify users when the player fails to render content in " +
      "an unexpected way.",
    eventNames: ["failed-to-render*"],
    frequency: 1,
    priorities: [],
    sources: [APP_ID_SINGLE_ZONE_SCHEDULER],
    threshold: 1,
  },
];

const priorities = {
  "0": {
    style: "success",
    label: "Information",
  },
  "1": {
    style: "warning",
    label: "Warning",
  },
  "2": {
    style: "danger",
    label: "Critical",
  },
};

const alertPresets = alertPresetsList
  .map((p) => {
    const { label } = p;

    return {
      value: label,
      label,
    };
  })
  .sort(sortByLabelAsc);

const alertTransformMapping = {
  min_occurrence: "frequency",
  severity_levels: "priorities",
  source_ids: "sources",
  target_events: "eventNames",
  time_range: "threshold",
};

const emptyAlert = {
  ...customAlertPreset,
  createResolvableIncident: false,
  targetDevices: [],
  userIds: [],
};

const groupedPresetOptions = [
  {
    label: "Custom Alert",
    options: [customAlertPreset],
  },
  {
    label: "Predefined Alerts",
    options: alertPresets,
  },
];

export {
  alertPresets,
  alertPresetsList,
  alertTransformMapping,
  customAlertPreset,
  DATE_RANGE_FIELD_NAME,
  DATE_RANGE_MAX_DAYS,
  emptyAlert,
  groupedPresetOptions,
  priorities,
  SEVERITY_CRIT,
  SEVERITY_INFO,
  SEVERITY_OPTIONS,
  SEVERITY_WARN,
  sources,
  sourceOptionsMap,
};
